import React from 'react';
import { TrendingFlat as ArrowRightIcon } from '@mui/icons-material';
import { Box, Button, Chip, Grid, Paper, Typography, useTheme } from '@mui/material';

interface StatCardProps {
  label: string;
  value: string | number;
  trend?: string;
  trendType?: 'up' | 'down';
  qualitativeLabel?: string;
  statusChip?: string;
  showViewAll?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  trend,
  trendType,
  qualitativeLabel,
  statusChip,
  showViewAll,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: trend || statusChip || showViewAll ? 1.5 : 0 }}>
        {value}
      </Typography>

      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {statusChip && (
          <Chip
            label={statusChip}
            size="small"
            sx={{
              bgcolor: '#E8F5E9',
              color: '#2E7D32',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        )}
        {showViewAll && (
          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#2D336B',
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '0.7rem',
              height: 24,
              px: 1.5,
              '&:hover': { bgcolor: '#1E2248' },
            }}
          >
            View All
          </Button>
        )}
        {trend && (
          <Chip
            label={trend}
            size="small"
            variant="outlined"
            sx={{
              color: trendType === 'up' ? 'success.main' : 'success.main', // Both look green in image
              borderColor: 'divider',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        )}
        {qualitativeLabel && (
          <Chip
            label={qualitativeLabel}
            size="small"
            variant="outlined"
            sx={{
              color: 'success.main',
              borderColor: 'success.main',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        )}
        {(statusChip || showViewAll) && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <ArrowRightIcon sx={{ fontSize: 14 }} />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export const ProviderDetailStats: React.FC = () => {
  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      <Grid item xs={12} md={2}>
        <StatCard label="License Expiration Date" value="—" statusChip="Active" />
      </Grid>
      <Grid item xs={12} md={1.8}>
        <StatCard label="Notes Audited" value="0 Notes" showViewAll />
      </Grid>
      <Grid item xs={12} md={1.4}>
        <StatCard label="Compliance Rate" value="0%" trend="↑ 0.0%" trendType="up" />
      </Grid>
      <Grid item xs={12} md={1.4}>
        <StatCard label="CPT Accuracy" value="0%" trend="↑ 0.0%" trendType="up" />
      </Grid>
      <Grid item xs={12} md={1.4}>
        <StatCard label="Rejection Rate" value="0%" trend="↓ 0.0%" trendType="down" />
      </Grid>
      <Grid item xs={12} md={1.4}>
        <StatCard label="Total Revenue" value="$0" trend="↑ 0.0%" trendType="up" />
      </Grid>
      <Grid item xs={12} md={1.4}>
        <StatCard label="Avg Time to Submit" value="0 Days" qualitativeLabel="Great" />
      </Grid>
    </Grid>
  );
};

